import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpDown, ChevronDown, ChevronRight, Filter, Pencil, Plus, Search, Settings, Trash2 } from 'lucide-react';
import { governedMcpApi } from '../../governed-mcp-gateway/governedMcpApi';
import { splitAssetToolName } from '../../governed-mcp-gateway/governedMcpConstants';
import type { PublishedCapability } from '../../governed-mcp-gateway/governedMcpTypes';
import { SOURCE_META, SPACE_TABS } from '../dataModelsConstants';
import type {
  DataModel,
  DataSource,
  McpTool,
  ModelTable,
  PublishStatus,
  Space,
  SpaceTabId,
} from '../dataModelsTypes';
import { formatRelative } from '../dataModelsUtils';
import { logDataModels } from '../dataModelsLogger';
import DataModelCard from './DataModelCard';
import CatalogGovernancePanel from './CatalogGovernancePanel';
import DataModelsAuditPanel from './DataModelsAuditPanel';
import QueryLogsPanel from './QueryLogsPanel';
import SpaceOverview, { assetKind, sourcesForSpace } from './SpaceOverview';
import WorkspaceConnectionsPanel from './WorkspaceConnectionsPanel';
import { DialogShell, Menu, MenuItem, SpaceIconTile, StatusPill, ViewRowButton, ghostBtn, primaryBtn, searchClass, surfaces, tagTone } from './dataModelsUi';

export type ModelSort = 'name' | 'updated';
export type StatusFilter = 'all' | PublishStatus;

export default function SpaceWorkspace({
  isLight,
  space,
  models,
  tables,
  sources,
  tools,
  spaceTab,
  onSpaceTab,
  modelQuery,
  onModelQuery,
  statusFilter,
  onStatusFilter,
  sortMode,
  onSortMode,
  onEditSpace,
  onCreateModel,
  onDeleteSpace,
  onDeleteModel,
  onSelectTable,
  onSelectModel,
  connectionBusy = false,
  onAttachConnections,
  onDetachConnection,
}: {
  isLight: boolean;
  space: Space;
  models: DataModel[];
  tables: ModelTable[];
  sources: DataSource[];
  tools: McpTool[];
  spaceTab: SpaceTabId;
  onSpaceTab: (tab: SpaceTabId) => void;
  modelQuery: string;
  onModelQuery: (value: string) => void;
  statusFilter: StatusFilter;
  onStatusFilter: (value: StatusFilter) => void;
  sortMode: ModelSort;
  onSortMode: (value: ModelSort) => void;
  onEditSpace: () => void;
  onCreateModel: () => void;
  onDeleteSpace?: () => Promise<void>;
  onDeleteModel: (modelId: string) => Promise<void>;
  onSelectTable: (tableId: string) => void;
  onSelectModel: (modelId: string) => void;
  connectionBusy?: boolean;
  onAttachConnections?: (sources: DataSource[]) => Promise<void>;
  onDetachConnection?: (sourceId: string) => Promise<void>;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const border = ui.border;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canDelete = models.length === 0;
  const vaultSources = sourcesForSpace(tables, sources, space.sourceIds);

  return (
    <section className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${ui.tabBar}`}>
      <div className="shrink-0 px-5 pb-2 pt-3">
        <div className={`mb-2 flex items-center gap-1 text-xs ${muted}`}>
          <span>WorkSpace</span>
          <ChevronRight size={12} />
          <span className={isLight ? 'text-[#1C1C1C]' : 'text-white'}>{space.name}</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <SpaceIconTile icon={space.icon} isLight={isLight} />
            <h2 className={`truncate text-lg font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{space.name}</h2>
            <StatusPill status={space.status} isLight={isLight} />
          </div>
          <div className="flex items-center gap-2">
            {canDelete && onDeleteSpace ? (
              <button
                type="button"
                className={ghostBtn(isLight)}
                title="Delete WorkSpace"
                aria-label={`Delete ${space.name}`}
                onClick={() => {
                  logDataModels('app', 'workspace_delete_opened', { spaceId: space.id });
                  setPendingDelete(true);
                }}
              >
                <Trash2 size={14} />
                Delete
              </button>
            ) : null}
            <button type="button" className={ghostBtn(isLight)} onClick={onEditSpace}>
              <Pencil size={14} />
              Edit
            </button>
            <button type="button" className={ghostBtn(isLight)} onClick={() => {
              logDataModels('app', 'vault_settings_opened', { spaceId: space.id });
              setSettingsOpen(true);
            }}>
              <Settings size={14} />
              Settings
            </button>
          </div>
        </div>
      </div>

      <div className={`flex shrink-0 gap-6 overflow-x-auto border-b px-5 ${border} ${ui.tabBar}`}>
        {SPACE_TABS.map((item) => {
          const Icon = item.icon;
          const active = spaceTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onSpaceTab(item.id)}
              className={`relative inline-flex shrink-0 items-center gap-2 py-2.5 text-sm ${
                active ? `font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}` : muted
              }`}
            >
              <Icon size={16} strokeWidth={1.75} />
              {item.label}
              {active ? <span className={`absolute inset-x-0 -bottom-px h-0.5 ${isLight ? 'bg-[#1C1C1C]' : 'bg-white'}`} /> : null}
            </button>
          );
        })}
      </div>

      <div className={`relative min-h-0 flex-1 ${ui.body}`}>
      <div className="absolute inset-0 overflow-y-auto overflow-x-hidden px-5 py-4">
        {spaceTab === 'overview' ? (
          <div className="space-y-4">
            <p className={`text-sm ${muted}`}>{space.description}</p>
            <SpaceOverview
            isLight={isLight}
            space={space}
            models={models}
            tables={tables}
            sources={sources}
            tools={tools}
            onCreateModel={onCreateModel}
            onSelectModel={onSelectModel}
            onOpenModels={() => onSpaceTab('models')}
            onOpenSources={() => onSpaceTab('connections')}
            onOpenAssets={() => onSpaceTab('assets')}
            onOpenActivity={() => onSpaceTab('activity')}
          />
          </div>
        ) : null}
        {spaceTab === 'models' ? (
          <ModelsPanel
            isLight={isLight}
            models={models}
            tables={tables}
            sources={sources}
            modelQuery={modelQuery}
            onModelQuery={onModelQuery}
            statusFilter={statusFilter}
            onStatusFilter={onStatusFilter}
            sortMode={sortMode}
            onSortMode={onSortMode}
            onCreateModel={onCreateModel}
            onDeleteModel={onDeleteModel}
            onSelectTable={onSelectTable}
            onSelectModel={onSelectModel}
          />
        ) : null}
        {spaceTab === 'connections' || spaceTab === 'sources' ? (
          <WorkspaceConnectionsPanel
            isLight={isLight}
            sourceIds={space.sourceIds ?? []}
            sources={sources}
            tables={tables}
            busy={connectionBusy}
            onAttach={onAttachConnections ?? (async () => undefined)}
            onDetach={onDetachConnection ?? (async () => undefined)}
          />
        ) : null}
        {spaceTab === 'mcp' ? <McpCatalog isLight={isLight} spaceId={space.id} models={models} tables={tables} /> : null}
        {spaceTab === 'assets' ? <AssetsCatalog isLight={isLight} tables={tables} sources={sources} onSelectTable={onSelectTable} /> : null}
        {spaceTab === 'governance' ? <CatalogGovernancePanel isLight={isLight} level="vault" space={space} /> : null}
        {spaceTab === 'activity' ? (
          <DataModelsAuditPanel
            isLight={isLight}
            workspaceId={space.id}
            streams={['workspace', 'catalog', 'asset', 'policy']}
          />
        ) : null}
        {spaceTab === 'queries' ? <QueryLogsPanel isLight={isLight} workspaceId={space.id} /> : null}
      </div>
      </div>
      {pendingDelete && onDeleteSpace ? (
        <DialogShell
          title="Delete WorkSpace"
          subtitle={`${space.name} has no catalogs and will be removed.`}
          isLight={isLight}
          onClose={() => (deleting ? undefined : setPendingDelete(false))}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} disabled={deleting} onClick={() => setPendingDelete(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={primaryBtn()}
                disabled={deleting}
                onClick={() => {
                  setDeleting(true);
                  logDataModels('app', 'workspace_delete_confirmed', { spaceId: space.id, name: space.name });
                  void onDeleteSpace()
                    .then(() => setPendingDelete(false))
                    .finally(() => setDeleting(false));
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p className={`text-sm ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
            This cannot be undone. WorkSpaces that still have catalogs cannot be deleted.
          </p>
        </DialogShell>
      ) : null}
      {settingsOpen ? (
        <DialogShell
          title="Settings"
          subtitle={space.name}
          isLight={isLight}
          onClose={() => setSettingsOpen(false)}
          footer={
            <button type="button" className={primaryBtn()} onClick={() => setSettingsOpen(false)}>
              Done
            </button>
          }
        >
          <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-y-2 text-sm">
            <dt className={muted}>Owner</dt>
            <dd className={isLight ? 'text-[#1C1C1C]' : 'text-white'}>{space.owner}</dd>
            <dt className={muted}>Status</dt>
            <dd><StatusPill status={space.status} isLight={isLight} /></dd>
            <dt className={muted}>Tags</dt>
            <dd className="flex flex-wrap gap-1.5">
              {space.tags.length ? space.tags.map((tag) => (
                <span key={tag} className={`rounded px-1.5 py-0.5 text-xs ${tagTone(tag, isLight)}`}>{tag}</span>
              )) : <span className={muted}>None</span>}
            </dd>
            <dt className={muted}>Sources</dt>
            <dd className={isLight ? 'text-[#1C1C1C]' : 'text-white'}>
              {vaultSources.length ? vaultSources.map((source) => source.name).join(', ') : 'None'}
            </dd>
          </dl>
        </DialogShell>
      ) : null}
    </section>
  );
}

function ModelsPanel({
  isLight,
  models,
  tables,
  sources,
  modelQuery,
  onModelQuery,
  statusFilter,
  onStatusFilter,
  sortMode,
  onSortMode,
  onCreateModel,
  onDeleteModel,
  onSelectTable,
  onSelectModel,
}: {
  isLight: boolean;
  models: DataModel[];
  tables: ModelTable[];
  sources: DataSource[];
  modelQuery: string;
  onModelQuery: (value: string) => void;
  statusFilter: StatusFilter;
  onStatusFilter: (value: StatusFilter) => void;
  sortMode: ModelSort;
  onSortMode: (value: ModelSort) => void;
  onCreateModel: () => void;
  onDeleteModel: (modelId: string) => Promise<void>;
  onSelectTable: (tableId: string) => void;
  onSelectModel: (modelId: string) => void;
}) {
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [collapsedAssetIds, setCollapsedAssetIds] = useState<string[]>([]);
  const [pendingDelete, setPendingDelete] = useState<DataModel | null>(null);
  const [deleting, setDeleting] = useState(false);
  const query = modelQuery.trim().toLowerCase();
  const visibleModels = models
    .filter((model) => (statusFilter === 'all' ? true : model.status === statusFilter))
    .filter((model) => {
      if (!query) return true;
      return `${model.name} ${model.description} ${model.tags.join(' ')}`.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      if (a.starred !== b.starred) return a.starred ? -1 : 1;
      if (sortMode === 'updated') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return a.name.localeCompare(b.name);
    });

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className={`text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Data models
          <span className={`ml-2 text-[13px] font-normal ${muted}`}>{visibleModels.length}</span>
        </h3>
        <button
          type="button"
          className={primaryBtn('!h-7 !px-2.5 gap-1 text-[12px]')}
          title="Add DataModel"
          aria-label="Add DataModel"
          onClick={onCreateModel}
        >
          <Plus size={12} strokeWidth={2.25} />
          Add DataModel
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
          <input
            value={modelQuery}
            onChange={(event) => onModelQuery(event.target.value)}
            placeholder="Search data models..."
            className={searchClass(isLight)}
          />
        </div>
        <div className="relative">
          <button type="button" className={ghostBtn(isLight)} onClick={() => setFilterOpen((open) => !open)}>
            <Filter size={14} />
            {statusFilter === 'all' ? 'Filter' : statusFilter === 'published' ? 'Published' : 'Draft'}
          </button>
          <Menu open={filterOpen} onClose={() => setFilterOpen(false)} isLight={isLight}>
            {(['all', 'published', 'draft'] as const).map((value) => (
              <MenuItem
                key={value}
                isLight={isLight}
                onClick={() => {
                  onStatusFilter(value);
                  setFilterOpen(false);
                }}
              >
                {value === 'all' ? 'All statuses' : value === 'published' ? 'Published' : 'Draft'}
              </MenuItem>
            ))}
          </Menu>
        </div>
        <div className="relative">
          <button type="button" className={ghostBtn(isLight)} onClick={() => setSortOpen((open) => !open)}>
            <ArrowUpDown size={14} />
            Sort · {sortMode === 'name' ? 'Name' : 'Updated'}
          </button>
          <Menu open={sortOpen} onClose={() => setSortOpen(false)} isLight={isLight}>
            <MenuItem
              isLight={isLight}
              onClick={() => {
                onSortMode('name');
                setSortOpen(false);
              }}
            >
              Name
            </MenuItem>
            <MenuItem
              isLight={isLight}
              onClick={() => {
                onSortMode('updated');
                setSortOpen(false);
              }}
            >
              Last updated
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="space-y-3">
        {visibleModels.map((model) => (
          <DataModelCard
            key={model.id}
            isLight={isLight}
            model={model}
            tables={tables.filter((table) => table.modelId === model.id)}
            sources={sources}
            expanded={!collapsedAssetIds.includes(model.id)}
            onToggle={() => setCollapsedAssetIds((prev) => (prev.includes(model.id) ? prev.filter((id) => id !== model.id) : [...prev, model.id]))}
            onSelectTable={onSelectTable}
            onOpen={() => onSelectModel(model.id)}
            onDelete={tables.some((table) => table.modelId === model.id) ? undefined : () => setPendingDelete(model)}
          />
        ))}
        {!visibleModels.length ? <p className={`py-10 text-center text-sm ${muted}`}>No data models match this filter.</p> : null}
      </div>

      {pendingDelete ? (
        <DialogShell
          title="Delete data model"
          subtitle={`${pendingDelete.name} has no assets and will be removed from this WorkSpace.`}
          isLight={isLight}
          onClose={() => (deleting ? undefined : setPendingDelete(null))}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} disabled={deleting} onClick={() => setPendingDelete(null)}>Cancel</button>
              <button
                type="button"
                className={primaryBtn()}
                disabled={deleting}
                onClick={() => {
                  const modelId = pendingDelete.id;
                  setDeleting(true);
                  logDataModels('app', 'model_delete_confirmed', { modelId, name: pendingDelete.name });
                  void onDeleteModel(modelId)
                    .then(() => setPendingDelete(null))
                    .finally(() => setDeleting(false));
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p className={`text-sm ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
            This cannot be undone. Models that still have assets cannot be deleted.
          </p>
        </DialogShell>
      ) : null}
    </div>
  );
}

function bindsToWorkspace(item: PublishedCapability, tableIds: Set<string>, modelIds: Set<string>): boolean {
  const selected = item.selectedTableIds ?? [];
  if (selected.some((id) => tableIds.has(id))) return true;
  if (item.sourceId && modelIds.has(item.sourceId)) return true;
  const { assetId } = splitAssetToolName(item.mcpToolName);
  return Boolean(assetId && tableIds.has(assetId));
}

function catalogLabel(item: PublishedCapability, tables: ModelTable[]): { asset: string; kind: string; model: string } {
  const selectedId = (item.selectedTableIds ?? [])[0] || splitAssetToolName(item.mcpToolName).assetId;
  const table = tables.find((entry) => entry.id === selectedId);
  return {
    asset: item.assetName?.trim() || table?.name || '—',
    kind: item.assetKind?.trim() || table?.assetKind || 'Table',
    model: item.sourceName?.trim() || '—',
  };
}

function McpCatalog({
  isLight,
  spaceId,
  models,
  tables,
}: {
  isLight: boolean;
  spaceId: string;
  models: DataModel[];
  tables: ModelTable[];
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const [rows, setRows] = useState<PublishedCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const tableKey = tables.map((table) => table.id).join(',');
  const modelKey = models.map((model) => `${model.id}:${model.name}`).join(',');

  useEffect(() => {
    let cancelled = false;
    const tableIds = new Set(tables.map((table) => table.id));
    const modelIds = new Set(models.map((model) => model.id));
    setLoading(true);
    setError(null);
    void governedMcpApi.getState()
      .then((state) => {
        if (cancelled) return;
        const published = (state.published ?? []).filter((item) => item.status === 'published' && bindsToWorkspace(item, tableIds, modelIds));
        setRows(published);
        logDataModels('app', 'workspace_mcp_listed', { spaceId, count: published.length });
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load published MCP tools';
        setRows([]);
        setError(message);
        logDataModels('error', 'workspace_mcp_list_failed', { spaceId, message });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [spaceId, tableKey, modelKey, tables, models]);

  const listed = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows
      .map((item) => {
        const labels = catalogLabel(item, tables);
        const model = labels.model !== '—' ? labels.model : (models.find((entry) => entry.id === item.sourceId)?.name ?? '—');
        return {
          id: item.id,
          tool: splitAssetToolName(item.mcpToolName).slug || item.mcpToolName,
          asset: labels.asset,
          kind: labels.kind,
          model,
          status: 'published' as const,
        };
      })
      .filter((row) => {
        if (!needle) return true;
        return `${row.tool} ${row.asset} ${row.model} ${row.kind}`.toLowerCase().includes(needle);
      })
      .sort((a, b) => a.tool.localeCompare(b.tool));
  }, [models, query, rows, tables]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={`text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Published MCP
          <span className={`ml-2 text-[13px] font-normal ${muted}`}>{loading ? '…' : listed.length}</span>
        </h3>
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search tools, catalog, models…"
            className={searchClass(isLight)}
          />
        </div>
      </div>
      {loading ? <p className={`py-8 text-sm ${muted}`}>Loading published MCP tools…</p> : null}
      {!loading && error ? <p className={`py-8 text-sm ${isLight ? 'text-red-700' : 'text-red-300'}`}>{error}</p> : null}
      {!loading && !error && !listed.length ? (
        <p className={`py-8 text-sm ${muted}`}>No published MCP tools in this WorkSpace catalog.</p>
      ) : null}
      {!loading && !error && listed.length ? (
        <div className={`overflow-hidden rounded-2xl border ${ui.card}`}>
          <table className="min-w-full text-[13px]">
            <thead className={ui.tableHead}>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5">Tool</th>
                <th className="px-3 py-2.5">Catalog</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Data Model</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {listed.map((row) => (
                <tr key={row.id} className={`border-t ${ui.border}`}>
                  <td className={`px-3 py-2.5 font-mono font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{row.tool}</td>
                  <td className={`px-3 py-2.5 ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{row.asset}</td>
                  <td className={`px-3 py-2.5 ${muted}`}>{row.kind}</td>
                  <td className={`px-3 py-2.5 ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{row.model}</td>
                  <td className="px-3 py-2.5">
                    <StatusPill status={row.status} isLight={isLight} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function AssetsCatalog({
  isLight,
  tables,
  sources,
  onSelectTable,
}: {
  isLight: boolean;
  tables: ModelTable[];
  sources: DataSource[];
  onSelectTable: (tableId: string) => void;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  if (!tables.length) return <p className={`py-8 text-sm ${muted}`}>No assets in this WorkSpace.</p>;
  return (
    <div className={`overflow-hidden rounded-2xl border ${ui.card}`}>
      <table className="min-w-full text-[13px]">
        <thead className={ui.tableHead}>
          <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
            <th className="px-3 py-2.5">Name</th>
            <th className="px-3 py-2.5">Type</th>
            <th className="px-3 py-2.5">Source</th>
            <th className="px-3 py-2.5">Last Updated</th>
            <th className="w-10 px-3 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {tables.map((table) => {
            const source = sources.find((item) => item.id === table.sourceId);
            return (
              <tr key={table.id} className={`border-t ${ui.border} ${ui.row}`}>
                <td className={`px-3 py-2.5 font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{table.name}</td>
                <td className={`px-3 py-2.5 ${muted}`}>{assetKind(table, sources)}</td>
                <td className={`px-3 py-2.5 ${muted}`}>{source ? SOURCE_META[source.kind].label : '—'}</td>
                <td className={`px-3 py-2.5 ${muted}`}>{formatRelative(table.updatedAt)}</td>
                <td className="px-3 py-2.5">
                  <ViewRowButton isLight={isLight} label={`View ${table.name}`} onClick={() => onSelectTable(table.id)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

